import { Component, inject, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { CustomersService } from '../../../services/customers/customers.service';
import { IUserInsight } from '../../../model/models';
import { ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexPlotOptions, ApexStroke, ApexTooltip, ApexXAxis, NgApexchartsModule } from 'ng-apexcharts';

export type FeatureChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  plotOptions: ApexPlotOptions;
  dataLabels: ApexDataLabels;
  stroke?: ApexStroke;
  tooltip?: ApexTooltip;
};

@Component({
  selector: 'customer-dashboard',
  standalone: true,
  imports: [CommonModule, NzTableModule, NzSkeletonModule, NzIconModule, NzToolTipModule, NzButtonModule, NzTagModule, NzEmptyModule, NgApexchartsModule],
  templateUrl: './customer-dashboard.component.html',
  styleUrl: './customer-dashboard.component.scss'
})
export class CustomerDashboardComponent implements OnInit {
  private customersService = inject(CustomersService);
  @Input() customer: any;
  @Input() users: any[] = [];
  @Input() keyAccounts: any[] = [];
  insights: IUserInsight[] = [];
  isLoading = true;
  dau = 0;
  wau = 0;
  mau = 0;
  totalAccounts = 0;
  activeRate = 0;
  featureUsage: { key: string, count: number }[] = [];
  featureChartOptions?: FeatureChartOptions;
  userFeatureChartOptions?: FeatureChartOptions;
  userChartType: 'bar' | 'line' = 'bar';
  private keyAccountEmails = new Set<string>();

  ngOnInit(): void {
    this.customersService.getUserInsights(this.customer.pk).subscribe({
      next: res => {
        this.insights = (res ?? []).filter(u => !this.isInternalUser(u.email));
        this.calculate();
        this.isLoading = false;
      },
      error: () => this.isLoading = false
    });
  }

  private calculate(): void {
    this.keyAccountEmails = new Set((this.keyAccounts ?? []).map(k => k.email?.toLowerCase()));
    const regular = this.insights.filter(u => !this.isKeyAccount(u.email));
    this.dau = regular.filter(u => u.count_24h > 0).length;
    this.wau = regular.filter(u => u.count_7d > 0).length;
    this.mau = regular.filter(u => u.count_30d > 0).length;
    this.totalAccounts = (this.users ?? [])
      .filter(u => !this.isKeyAccount(u.email) && !this.isInternalUser(u.email)).length;
    this.activeRate = this.totalAccounts ? this.mau / this.totalAccounts : 0;

    const totals = new Map<string, number>();
    this.insights.forEach(u => Object.entries(u.features ?? {})
      .forEach(([key, count]) => totals.set(key, (totals.get(key) ?? 0) + count)));
    this.featureUsage = [...totals].map(([key, count]) => ({ key, count }))
      .sort((a, b) => b.count - a.count);
    this.buildFeatureChart();
    this.buildUserFeatureChart();
  }

  setUserChartType(type: 'bar' | 'line'): void {
    this.userChartType = type;
    this.buildUserFeatureChart();
  }

  private buildFeatureChart(): void {
    this.featureChartOptions = {
      series: [{ name: 'Total opens', data: this.featureUsage.map(f => f.count) }],
      chart: { type: 'bar', height: Math.max(200, this.featureUsage.length * 40), toolbar: { show: false } },
      xaxis: { categories: this.featureUsage.map(f => this.featureLabel(f.key)) },
      plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
      dataLabels: { enabled: true }
    };
  }

  private buildUserFeatureChart(): void {
    const total = (u: IUserInsight) => Object.values(u.features ?? {}).reduce((sum, n) => sum + n, 0);
    const users = this.insights.filter(u => total(u) > 0).sort((a, b) => total(b) - total(a));
    const featureKeys = this.featureUsage.map(f => f.key).sort();
    const isBar = this.userChartType === 'bar';
    this.userFeatureChartOptions = {
      series: featureKeys.map(key => ({
        name: this.featureLabel(key),
        data: users.map(u => u.features?.[key] ?? 0)
      })),
      chart: { type: this.userChartType, height: 400, stacked: isBar, toolbar: { show: false } },
      xaxis: {
        categories: users.map(u => this.chartLabel(u.email)),
        labels: {
          rotate: -45,
          trim: true,
          style: { colors: users.map(u => this.isKeyAccount(u.email) ? '#d48806' : this.isDecisionMaker(u.email) ? '#1890ff' : '#373d3f') }
        }
      },
      plotOptions: {
        bar: { horizontal: false, dataLabels: { total: { enabled: true } } }
      },
      dataLabels: { enabled: false },
      stroke: isBar ? { width: 0 } : { width: 2, curve: 'straight' },
      tooltip: {
        custom: ({ seriesIndex, dataPointIndex }: any) => this.userFeatureTooltip(users[dataPointIndex], featureKeys[seriesIndex])
      }
    };
  }

  private userFeatureTooltip(user: IUserInsight, hoveredKey: string): string {
    const features = Object.entries(user.features ?? {}).sort(([a], [b]) => a.localeCompare(b));
    const total = features.reduce((sum, [, n]) => sum + n, 0);
    const rows = features.map(([key, count]) => `
      <div class="feature${key === hoveredKey ? ' active' : ''}">
        <span>${this.escape(this.featureLabel(key))}</span><strong>${count}</strong>
      </div>`).join('');
    return `
      <div class="user-insights-tooltip">
        <div class="email">${this.escape(user.email)}</div>
        ${this.isKeyAccount(user.email) ? '<div class="ka">Key Account</div>' : ''}
        ${this.isDecisionMaker(user.email) ? `<div class="dm">${this.escape(this.decisionMakerLabel())}</div>` : ''}
        <div class="meta">24h: ${user.count_24h} · 7d: ${user.count_7d} · 30d: ${user.count_30d}</div>
        ${rows}
        <div class="total"><span>Total</span><strong>${total}</strong></div>
      </div>
    `;
  }

  private escape(value: string): string {
    return String(value ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
  }

  // internal yoma users are excluded from all statistics
  private isInternalUser(email: string): boolean {
    return email?.toLowerCase().includes('@yoma') ?? false;
  }

  isKeyAccount(email: string): boolean {
    return this.keyAccountEmails.has(email?.toLowerCase());
  }

  isDecisionMaker(email: string): boolean {
    const dm = this.customer?.decision_maker_email;
    return !!dm && email?.toLowerCase() === dm.toLowerCase();
  }

  decisionMakerLabel(): string {
    const role = this.customer?.decision_maker_role;
    return role ? `Decision Maker · ${role}` : 'Decision Maker';
  }

  private chartLabel(email: string): string {
    const marks = [this.isKeyAccount(email) && 'KA', this.isDecisionMaker(email) && 'DM'].filter(Boolean);
    return marks.length ? `${email} (${marks.join(', ')})` : email;
  }

  featureLabel(key: string): string {
    return key.replace(/^[A-Z]+\.\s*/, '').replace(/:\s*Menu item clicked$/, '');
  }
}
